import React, { useCallback, useState } from 'react';

import { createCredentialBundle } from '@/sections/asset/components/tabs/credentials/credentialForms/createCredentialBundle';
import {
  type ApiKeyFormData,
  type BasicCredentialFormData,
  type BearerTokenFormData,
  type ComplexLoginMultiFormData,
  CredentialType,
  type RecordingCredentialFormData,
  type StaticTokenFormData,
} from '@/sections/asset/components/tabs/credentials/credentialForms/types';

interface UseCredentialFormStateOptions {
  isWebService: boolean;
  /** Pre-fills the Complex-Login login URL from the owning WebApplication. */
  webAppUrl: string;
}

export interface CredentialFormState {
  selectedType: CredentialType;
  setSelectedType: React.Dispatch<React.SetStateAction<CredentialType>>;
  basicForm: BasicCredentialFormData;
  setBasicForm: React.Dispatch<React.SetStateAction<BasicCredentialFormData>>;
  recordingForm: RecordingCredentialFormData;
  setRecordingForm: React.Dispatch<
    React.SetStateAction<RecordingCredentialFormData>
  >;
  bearerTokenForm: BearerTokenFormData;
  setBearerTokenForm: React.Dispatch<React.SetStateAction<BearerTokenFormData>>;
  apiKeyForm: ApiKeyFormData;
  setApiKeyForm: React.Dispatch<React.SetStateAction<ApiKeyFormData>>;
  staticTokenForm: StaticTokenFormData;
  setStaticTokenForm: React.Dispatch<React.SetStateAction<StaticTokenFormData>>;
  complexLoginMulti: ComplexLoginMultiFormData;
  setComplexLoginMulti: React.Dispatch<
    React.SetStateAction<ComplexLoginMultiFormData>
  >;
  /** Resets every per-type form (and the selected type) back to initial. */
  reset: () => void;
}

/**
 * useCredentialFormState bundles the per-type credential form state that was
 * previously inlined in `WebAuthentication`. The default selected type depends
 * on the asset type — web services get the 4-option flow rooted at BASIC; web
 * applications get the 3-option flow rooted at STATIC_TOKEN.
 */
export function useCredentialFormState({
  isWebService,
  webAppUrl,
}: UseCredentialFormStateOptions): CredentialFormState {
  // R-5: the asset default type is BASIC (web service) / STATIC_TOKEN (web app).
  // It is NOT COMPLEX_LOGIN — that default is wizard-only.
  const defaultType = isWebService
    ? CredentialType.BASIC
    : CredentialType.STATIC_TOKEN;

  // Lazy initial bundle: every per-type form starts at its INITIAL constant and
  // COMPLEX_LOGIN's login URL is pre-filled from webAppUrl. Shared with the
  // wizard via createCredentialBundle (the bundle has no recipeForm — this hook
  // doesn't carry one either).
  const [selectedType, setSelectedType] = useState<CredentialType>(
    () => createCredentialBundle(defaultType, webAppUrl).selectedType
  );
  const [basicForm, setBasicForm] = useState<BasicCredentialFormData>(
    () => createCredentialBundle(defaultType, webAppUrl).basicForm
  );
  const [recordingForm, setRecordingForm] =
    useState<RecordingCredentialFormData>(
      () => createCredentialBundle(defaultType, webAppUrl).recordingForm
    );
  const [bearerTokenForm, setBearerTokenForm] = useState<BearerTokenFormData>(
    () => createCredentialBundle(defaultType, webAppUrl).bearerTokenForm
  );
  const [apiKeyForm, setApiKeyForm] = useState<ApiKeyFormData>(
    () => createCredentialBundle(defaultType, webAppUrl).apiKeyForm
  );
  const [staticTokenForm, setStaticTokenForm] = useState<StaticTokenFormData>(
    () => createCredentialBundle(defaultType, webAppUrl).staticTokenForm
  );
  const [complexLoginMulti, setComplexLoginMulti] =
    useState<ComplexLoginMultiFormData>(
      () => createCredentialBundle(defaultType, webAppUrl).complexLoginMulti
    );

  const reset = useCallback(() => {
    const fresh = createCredentialBundle(defaultType, webAppUrl);
    setSelectedType(fresh.selectedType);
    setBasicForm(fresh.basicForm);
    setRecordingForm(fresh.recordingForm);
    setBearerTokenForm(fresh.bearerTokenForm);
    setApiKeyForm(fresh.apiKeyForm);
    setStaticTokenForm(fresh.staticTokenForm);
    setComplexLoginMulti(fresh.complexLoginMulti);
  }, [defaultType, webAppUrl]);

  return {
    selectedType,
    setSelectedType,
    basicForm,
    setBasicForm,
    recordingForm,
    setRecordingForm,
    bearerTokenForm,
    setBearerTokenForm,
    apiKeyForm,
    setApiKeyForm,
    staticTokenForm,
    setStaticTokenForm,
    complexLoginMulti,
    setComplexLoginMulti,
    reset,
  };
}
